import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileInput, Radio } from '@mantine/core';
import * as XLSX from 'xlsx';

import Point from '@arcgis/core/geometry/Point';
import * as projection from '@arcgis/core/geometry/projection';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';

import {
  tokenSelector,
  userHasPermissionsSelector,
  usernameSelector,
  UserRole,
  userRoleSelector,
} from '../../store/slices/authSlice';
import {
  disclaimerAcceptedSelector,
  privacyPolicyAcceptedSelector,
  setLoadingMessage,
  setShowGlobalLoading,
} from '../../store/slices/appSlice';
import {
  latitudeSelector,
  longitudeSelector,
  originUploadedFileSelector,
  selectedCountrySelector,
} from '../../store/slices/verificationSlice';

import useAuthenticate from '../../customHooks/authenticate';

import { AnalysisType, createLogRecord, queryFeatures } from '../../services/esri';
import { NotificationType, showNotification } from '../../utils/showNotifications';
import { executeAPIStatus, executeAPIVerification } from '../../services/aws';
import { retryRequest } from '../../utils/retryRequest';
import { calculateTotalArea } from '../../utils/calculateArea';

import GlobalLoader from '../../components/GlobalLoader/GlobalLoader';
import WFIDButton from '../../components/WFIDButton/WFIDButton';
import RenderCoordInputFields from './RenderCoordInputFields/RenderCoordInputFields';
import RenderCountrySelect from './RenderCountrySelect/RenderCountrySelect';
import RenderFileUpload, { UploadFileOptions } from './RenderFileUpload/RenderFileUpload';
import ShowResult from '../../components/ShowResult/ShowResult';
import DownloadSampleData from '../../components/DownloadSampleData/DownloadSampleData';
import DisclaimersComponent from '../../components/DisclaimersComponent/DisclaimersComponent';

import useGetSpeciesData from '../../customHooks/useGetSpeciesData';
import { mapControllerVerification } from '../../controllers/MapControllerVerification';

import { LAYER_IDS } from '../../config/layer.config';
import { Method } from '../../config/s3publicfiles.config';
import { definitionParams } from '../../config/esriItemFields';
import 'pdfmake/build/vfs_fonts';
import { DISCLAIMER_URL } from '../../config/assets.url.config';
import {
  disableVerifyButton,
  extractGenusData,
  findCountryFeature,
  generateGeoJSONurl,
  getBufferGeometry,
  getPolygonData,
  handleFileChange,
  handlePrintReport,
  handleReset,
  handleSelectionMethod,
  isValidCoordinate,
  testIsWithin,
  updateLayerUrl,
  updateDefinition,
} from './helpers';
import { nanoid } from 'nanoid';
import { getErrorMessageBasedOnCode, StatusCode } from '../helpers';

import '../index.css';
import { GeoJSONgeneric, loadCountryparams, ORIGIN_TYPE_OPTIONS, OriginType, WORLD_COUNTRIES } from './types';

const Verification = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [selectedOption, setSelectedOption] = useState<OriginType | null>(null); // State to keep track of the selected radio button option
  const [countries, setCountries] = useState<string[]>([]); // State to hold the list of countries
  const selectedCountry = useSelector(selectedCountrySelector);
  const countriesLoaded = useRef(false);
  const [selectedMethod, setSelectedMethod] = useState(Method.SIRA);

  const latitude = useSelector(latitudeSelector);
  const longitude = useSelector(longitudeSelector);

  const originUploadedFile = useSelector(originUploadedFileSelector);
  const [file, setFile] = useState<File | null>(null);
  const [showPostResultInfo, setShowPostResultInfo] = useState(false);
  const [postResultInfoMessage, setPostResultInfoMessage] = useState('');
  const [genusData, setGenusData] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false); // Add loading state
  const applicationDiv = useRef(null);
  const [selectedGenus, setSelectedGenus] = useState<string>('');

  const [testSampleDataFromFile, setTestSampleDataFromFile] = useState<any>([]);
  const [fileDataHashMap, setFileDataHashMap] = useState<any>(null);
  const [certificationStatus, setCertificationStatus] = useState<string>('Prefer not to say');

  const [generatedFeatureServiceUrl, setGeneratedFeatureServiceUrl] = useState<string | null>(null);

  const [currentAnalysisId, setCurrentAnalysisId] = useState<string>('');
  const [canUserRunAnalysis, setCanUserRunAnalysis] = useState(false);

  const username = useSelector(usernameSelector);
  const userRole = useSelector(userRoleSelector);
  const token = useSelector(tokenSelector);
  const disclaimerAccepted = useSelector(disclaimerAcceptedSelector);
  const privacyPolicyAccepted = useSelector(privacyPolicyAcceptedSelector);
  const userHasPermissions = useSelector(userHasPermissionsSelector);

  const dispatch = useDispatch();

  const userCredentials = useAuthenticate();

  useGetSpeciesData();

  useEffect(() => {
    if (!userRole) return;

    if (userRole !== UserRole.OrgAdmin && userRole !== UserRole.OrgPublisher) {
      showNotification({
        type: NotificationType.Warning,
        title: 'Warning',
        message: 'You do not have the correct permissions to run the analysis. If you think this is a mistake, please contact support.',
      });
    } else {
      setCanUserRunAnalysis(true);
    }
  }, [userRole]);

  useEffect(() => {
    // loadCounty Effects
    const loadCountries = async () => {
      const results = await queryFeatures(loadCountryparams, WORLD_COUNTRIES.url);
      if (results.success) {
        const countryNames = results.data?.features.map((feature) => feature.attributes.Name) as string[];
        setCountries(countryNames);
        countriesLoaded.current = true;
      }
    };
    loadCountries();
    // extractGenus Effects
    extractGenusData(setGenusData);
    // mapController Effects
    mapControllerVerification.initializeMap(mapContainer, applicationDiv);
    return () => {
      mapControllerVerification.destroyMap();
    };
  }, [userCredentials]);

  const handleUploadButtonClick = async () => {
    setLoading(true);
    dispatch(setShowGlobalLoading(true));
    let countryFeature: any, isWithin: any;
    let claimedLocation: string | null = '';
    let geoJSON: GeoJSONgeneric | null = null;
    setShowPostResultInfo(false);
    setPostResultInfoMessage('');

    await projection.load();
    const getDistributionData = await mapControllerVerification.queryLayer(LAYER_IDS.GENUS_RANGE);
    
    // Remove any existing claimed harvest origin layer
    mapControllerVerification.removeClaimedHarvestOriginLayer();
    
    if (selectedOption === 'Shapefile') {
      geoJSON = originUploadedFile ?? null;
      claimedLocation = 'Shapefile';
      if (geoJSON) {
        const geoJSONurl = generateGeoJSONurl(geoJSON);
        const { geoJSONFeature } = await mapControllerVerification.addGeoJsonFileToMap(geoJSONurl);
        countryFeature = geoJSONFeature.features[0];
        
        // Add as claimed harvest origin layer
        await mapControllerVerification.addClaimedHarvestOriginLayer(countryFeature.geometry, 'Claimed Harvest Origin (Shapefile)');

        projection.load().then(async () => {
          const projFeature = projection.project(countryFeature.geometry, new SpatialReference({ wkid: 102100 }));
          isWithin = await testIsWithin(projFeature, getDistributionData, dispatch);
          if (!isWithin) return;
        });
      }
    } else if (selectedOption === 'GeoJSON') {
      geoJSON = originUploadedFile ?? null;
      if (geoJSON) {
        const geoJSONurl = generateGeoJSONurl(geoJSON);
        const { geoJSONFeature } = await mapControllerVerification.addGeoJsonFileToMap(geoJSONurl);
        countryFeature = geoJSONFeature.features[0];
        claimedLocation = 'GeoJSON';
        
        // Add as claimed harvest origin layer
        await mapControllerVerification.addClaimedHarvestOriginLayer(countryFeature.geometry, 'Claimed Harvest Origin (GeoJSON)');

        projection.load().then(async () => {
          const projFeature = projection.project(countryFeature.geometry, new SpatialReference({ wkid: 102100 }));
          isWithin = await testIsWithin(projFeature, getDistributionData, dispatch);
          if (!isWithin) return;
        });
      }
    } else if (selectedOption === 'Country') {
      countryFeature = await findCountryFeature(selectedCountry || '');
      claimedLocation = selectedCountry;
      
      // Add as claimed harvest origin layer
      await mapControllerVerification.addClaimedHarvestOriginLayer(countryFeature.geometry, `Claimed Harvest Origin (${selectedCountry})`);
      
      const isWithin = await testIsWithin(countryFeature.geometry, getDistributionData, dispatch);
      if (!isWithin) return;
    } else if (selectedOption === 'Coordinates') {
      if (latitude && longitude) {
        const parsedLat = parseFloat(latitude as string);
        const parsedLong = parseFloat(longitude as string);
        const isInputCoordinateValid = isValidCoordinate(parsedLat, parsedLong);
        const stringCoordinates = JSON.stringify({ lat: parsedLat, long: parsedLong });
        claimedLocation = stringCoordinates;

        if (!isInputCoordinateValid) {
          showNotification({
            type: NotificationType.Error,
            title: 'Error',
            message: 'The claimed coordinates are invalid. Please enter valid coordinates.',
          });

          dispatch(setShowGlobalLoading(false));
          return;
        }
        const point = new Point({
          x: parsedLong,
          y: parsedLat,
          spatialReference: { wkid: 4326 },
        });
        const { bufferGeometry } = getBufferGeometry(point);
        
        // Add buffer as claimed harvest origin layer
        await mapControllerVerification.addClaimedHarvestOriginLayer(bufferGeometry, 'Claimed Harvest Origin (Coordinates)');
        
        projection.load().then(async () => {
          const projFeature = projection.project(bufferGeometry as any, new SpatialReference({ wkid: 102100 }));
          isWithin = await testIsWithin(projFeature, getDistributionData, dispatch);
          if (!isWithin) return;
        });

        countryFeature = { geometry: bufferGeometry };
      }
    }

    if (file) {
      const reader = new FileReader();

      reader.onload = async (event: ProgressEvent<FileReader>) => {
        if (!event.target) return;
        const arrayBuffer = event.target.result as ArrayBuffer;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        dispatch(setLoadingMessage('Uploading file...'));
        const analysisID = nanoid();

        // if selection option is country, then get the country features on the server
        // payload fails if country feature is too large
        const projectedFeature = getPolygonData(selectedOption || '', countryFeature);

        try {
          if (!username || !token) return;
          const body = JSON.stringify({
            inputchoice: 'verification',
            authorization: token,
            username,
            body: jsonData, // This removes the base64 prefix (e.g., "data:text/csv;base64,")
            polygon: projectedFeature,
            isBase64Encoded: false,
            selectedOption: selectedOption,
            selectedCountry: selectedCountry,
            analysisID,
          });
          const startResponse = await executeAPIVerification({ token, body });

          dispatch(setLoadingMessage('Processing file...'));

          if (!startResponse.ok) {
            showNotification({
              type: NotificationType.Error,
              message: 'Failed to upload file. Please try again.',
              title: 'Error',
            });
            return;
          }

          const startData = await startResponse.json();
          const executionArn = startData.executionArn;

          // Poll the status endpoint to check execution status
          const pollStatus = async (executionArn: string) => {
            const statusResponse = await executeAPIStatus({
              token,
              executionArn,
            });

            if (!statusResponse.ok) {
              throw new Error('Failed to check execution status');
            }

            const statusData = await statusResponse.json();
            const status = statusData.status;

            if (status === 'RUNNING') {
              dispatch(setLoadingMessage('Processing data...'));
              setTimeout(() => pollStatus(executionArn), 5000);
            } else if (status === 'SUCCEEDED') {
              const output = JSON.parse(statusData.output.body);
              const statusCode = statusData?.output?.statusCode;
              const notifictionMessage = getErrorMessageBasedOnCode(statusCode);

              if (statusCode !== StatusCode.OK) {
                showNotification({
                  type: NotificationType.Error,
                  message: notifictionMessage,
                  title: 'Error',
                  autoClose: false,
                });
                setLoading(false);
                dispatch(setShowGlobalLoading(false));
                return;
              } else {
                const serviceUrl = output.publishResponse.services[0].serviceurl;

                const url = `${serviceUrl}/0`;
                const urlWitihToken = `${serviceUrl}/0?f=json&token=${token}`;
                const result = await retryRequest(urlWitihToken);

                const updatedUrl = updateLayerUrl(`${url}/updateDefinition`);
                // update definition for the feature service
                await updateDefinition({ token, definitionParams, defUrl: updatedUrl });
                setGeneratedFeatureServiceUrl(url);
                if (!result?.success) {
                  // show Error message
                  showNotification({
                    type: NotificationType.Error,
                    message: 'Error loading feature layers',
                    title: 'Error',
                  });
                  setLoading(false);
                  dispatch(setShowGlobalLoading(false));
                  return;
                }
                const params = { url, title: 'Evaluation result', id: '0.05-layer' };
                mapControllerVerification.addLayer(params);
                mapControllerVerification.toggleLayersVisibility();

                const polygonGridUrl = `https://services2.arcgis.com/HlCEjEqEP9GdOQeZ/arcgis/rest/services/${selectedGenus}_grid_50_clipped/FeatureServer/0`;
                const polygonGridParams = {
                  where: '1=1',
                  returnGeometry: true,
                  outFields: ['*'],
                };

                dispatch(setLoadingMessage('Loading data...'));
                const gridLayerInfo = await queryFeatures(polygonGridParams, polygonGridUrl);
                if (!gridLayerInfo.success) {
                  // show Error message
                  showNotification({
                    type: NotificationType.Error,
                    message: 'Error querying features. Please try again or contact support.',
                    title: 'Error',
                    autoClose: false,
                  });

                  setLoading(false);
                  dispatch(setShowGlobalLoading(false));
                  return;
                }
                const gridCalculatedValues = calculateTotalArea(gridLayerInfo.data);
                if (!gridCalculatedValues) {
                  // show Error message
                  showNotification({
                    type: NotificationType.Error,
                    message: 'Error calculating total area. Please try again.',
                    title: 'Error',
                    autoClose: false,
                  });
                  setLoading(false);
                  dispatch(setShowGlobalLoading(false));
                  return;
                }
                const layerInfo = await queryFeatures(polygonGridParams, polygonGridUrl);

                if (!layerInfo.success) {
                  // show Error message
                  showNotification({
                    type: NotificationType.Error,
                    message: 'Error querying features. Please try again.',
                    title: 'Error',
                    autoClose: false,
                  });

                  setLoading(false);
                  dispatch(setShowGlobalLoading(false));
                  return;
                }
                dispatch(setLoadingMessage('Calculating data...'));
                const infoCalculatedValues = calculateTotalArea(layerInfo.data);
                if (!infoCalculatedValues) {
                  // show Error message
                  showNotification({
                    type: NotificationType.Error,
                    message: 'Error calculating total area. Please try again.',
                    title: 'Error',
                    autoClose: false,
                  });
                  setLoading(false);
                  dispatch(setShowGlobalLoading(false));
                  return;
                }
                // create log record after the analysis is completed
                const logRecordParams = {
                  hashMap: fileDataHashMap,
                  featureUrl: url,
                  analysisID,
                  username,
                  type: AnalysisType.Verification,
                  ClaimedLocation: claimedLocation,
                  certificationStatus: certificationStatus,
                };
                setCurrentAnalysisId(analysisID);

                await createLogRecord(logRecordParams);

                setShowPostResultInfo(true);
                dispatch(setLoadingMessage(''));
                setLoading(false);
                dispatch(setShowGlobalLoading(false));
                setPostResultInfoMessage(
                  `The total number of possible harvest locations where values are inferred is ${infoCalculatedValues.totalPoints}, with each location representing an average area of ${infoCalculatedValues.averageAreaKm2GeodesicRounded}km². The total number of inferred locations is ${gridCalculatedValues.totalPoints} with an average area of ${gridCalculatedValues.averageAreaKm2GeodesicRounded}km².`
                );
              }
            } else {
              setLoading(false);
              dispatch(setLoadingMessage(''));
              dispatch(setShowGlobalLoading(false));
              throw new Error(`Step Function execution failed with status: ${status}`);
            }
          };

          pollStatus(executionArn);
        } catch (error) {
          setLoading(false);
          dispatch(setLoadingMessage(''));
          dispatch(setShowGlobalLoading(false));
          console.error('Error:', error);
        }
      };

      reader.readAsArrayBuffer(file); // Add this line to trigger the file read
    }
  };


  if (!userCredentials) return <div className='default-loader-message'>Loading...</div>;
  // Determine if user is admin for method selection
  const isAdmin = userRole === UserRole.OrgAdmin

  return (
    <div className='main-wrapper'>
      <GlobalLoader />

      <div className='left-side'>
        <p className='panel-description'>
          This evaluation platform allows you to compare chemical measurements from your supply chain sample with
          ground-truthed and inferred reference data for a growing number of countries and species.
        </p>
        <p className='panel-description'>
          The platform holds reference data for Stable Isotope Ratio Analysis (SIRA) and two methods of Multi-Element
          Analysis (MEA): XRF and ICPMS.
        </p>
        <p className='panel-description'>
          Currently, the platform can only compare measurements from one analysis method at a time, but we aim to
          develop the functionality to allow users to compare with multiple chemical reference sets simultaneously,
          pending funding.
        </p>

        <div className='analysis-container'>
          <div className='analysis-container-inner'>
            <p>
              <span className='method-selection-step'>1. </span>
              Download chemical data template
            </p>
            <p>
              Download the template spreadsheets to ensure that your chemical data is formatted correctly here. Please
              ensure that you have the latest format before uploading. All measurements should be taken by a laboratory
              using preparation and measurement protocols that are comparable with the reference data in the platform in
              order to ensure reliable results.
            </p>

            <DownloadSampleData />
                          <div className='certification-method-selection'>
                <p className=''>
                  <span className='method-selection-step'>2.</span> Select product certification
                </p>
                <p className=''>Choose the certification status of the product being evaluated.</p>
                               <div className='analysis-method-container'>
                 <div key='certified' className='radio-container'>
                   <Radio
                     onChange={() => setCertificationStatus('Certified')}
                     checked={certificationStatus === 'Certified'}
                     label='Certified'
                   />
                 </div>
                 <div key='not-certified' className='radio-container'>
                   <Radio
                     onChange={() => setCertificationStatus('Not certified')}
                     checked={certificationStatus === 'Not certified'}
                     label='Not certified'
                   />
                 </div>
                 <div key='prefer-not-to-say' className='radio-container'>
                   <Radio
                     onChange={() => setCertificationStatus('Prefer not to say')}
                     checked={certificationStatus === 'Prefer not to say'}
                     label='Prefer not to say'
                   />
                 </div>
               </div>
              </div>

            {canUserRunAnalysis && (
              <div className='analysis-method-selection'>
                <p className=''>
                  <span className='method-selection-step'>3. </span> Select chemical method
                </p>
                <p className=''>Choose which chemical method was used to generate your supply chain sample data.</p>
                <div className='analysis-method-container'>
                  <div key={Method.SIRA} className='radio-container'>
                    <Radio
                      onChange={() => handleSelectionMethod(Method.SIRA, setFile, setSelectedMethod)}
                      checked={selectedMethod === Method.SIRA}
                      label={Method.SIRA}
                    />
                  </div>
                  <div key={Method.SIRAXRF} className='radio-container'>
                    <Radio
                      onChange={() => handleSelectionMethod(Method.SIRAXRF, setFile, setSelectedMethod)}
                      checked={selectedMethod === Method.SIRAXRF}
                      label={Method.SIRAXRF}
                    />
                  </div>
                  {isAdmin && (
                    <>
                      <div key={Method.XRF} className='radio-container'>
                        <Radio
                          onChange={() => handleSelectionMethod(Method.XRF, setFile, setSelectedMethod)}
                          checked={selectedMethod === Method.XRF}
                          label={Method.XRF}
                        />
                      </div>
                      <div key={Method.ICPMS} className='radio-container'>
                        <Radio
                          onChange={() => handleSelectionMethod(Method.ICPMS, setFile, setSelectedMethod)}
                          checked={selectedMethod === Method.ICPMS}
                          label={Method.ICPMS}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            {canUserRunAnalysis && (
              <div className='upload-file-container'>
                <p className=''>
                  <span className='method-selection-step'>4. </span>
                  Upload chemical data
                </p>
                <p className=''>
                  Upload the formatted supply chain sample spreadsheet. You must use the spreadsheet template
                  corresponding to the selected chemical method.
                </p>
                <p className=''>
                  The spreadsheet must contain ONE sample only. Additional rows will be ignored or may prevent the tool
                  from running. We aim to develop bulk analysis functionality, pending funding.
                </p>
                <div className='file-upload-wrapper'>
                  <FileInput
                    placeholder='Choose file'
                    className='file-upload'
                    value={file}
                    withAsterisk
                    color='red'
                    variant='deafult'
                    clearable={!loading}
                    size='md'
                    w={120}
                    disabled={loading || !userHasPermissions}
                    accept='.xlsx'
                    onChange={(event) =>
                      handleFileChange(
                        event,
                        setShowPostResultInfo,
                        setPostResultInfoMessage,
                        setFile,
                        setFileDataHashMap,
                        setTestSampleDataFromFile,
                        setSelectedGenus,
                        selectedMethod,
                        genusData
                      )
                    }
                  />
                </div>
              </div>
            )}

            {canUserRunAnalysis && (
              <div className='analysis-method-selection'>
                <p className=''>
                  <span className='method-selection-step'>5. </span>
                  Specify the claimed location of harvest
                </p>
                <p className=''>
                  Specify the claimed harvest location of the supply chain sample by providing coordinates, selecting a
                  country, or uploading a geojson or shapefile.
                </p>
                <div className='analysis-method-container origin-type-container'>
                  {ORIGIN_TYPE_OPTIONS.map((method) => (
                    <div key={method} className='radio-container'>
                      <Radio
                        onChange={() => setSelectedOption(method)}
                        disabled={!userHasPermissions}
                        // handleOptionChange(method) previous function deleted
                        checked={selectedOption === method}
                        label={method}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {canUserRunAnalysis && (
              <div>
                {selectedOption === OriginType.Coordinates && <RenderCoordInputFields />}
                {selectedOption === OriginType.Country && <RenderCountrySelect countries={countries} />}
                {selectedOption === OriginType.GeoJSON && <RenderFileUpload type={UploadFileOptions.GeoJSON} />}
                {selectedOption === OriginType.Shapefile && <RenderFileUpload type={UploadFileOptions.Shapefile} />}
              </div>
            )}

            {/*Disclaimers component */}
            {canUserRunAnalysis && <DisclaimersComponent />}
            <div className='analyze-btn-container'>
              <WFIDButton
                variant='filled'
                color='#151515'
                width={130}
                disabled={disableVerifyButton(
                  disclaimerAccepted,
                  privacyPolicyAccepted,
                  file,
                  selectedOption,
                  latitude,
                  longitude,
                  selectedCountry,
                  originUploadedFile,
                  canUserRunAnalysis
                )}
                onClick={handleUploadButtonClick}
                content='Evaluate'
              />
            </div>
            <div className='reset-fields-btn'>
              <WFIDButton
                width={130}
                bgColor='#FAFAFA'
                onClick={() =>
                  handleReset(setPostResultInfoMessage, setShowPostResultInfo, setFile, setSelectedOption, dispatch)
                }
                content='Reset Fields'
              />
            </div>
          </div>
        </div>

        <div className='print-report-container'>
          <div className='disclaimer-container'>
            <a rel='noreferrer' target='_blank' href={DISCLAIMER_URL}>
              Disclaimer
            </a>
          </div>
          <button
            disabled={!showPostResultInfo}
            type='button'
            onClick={() =>
              handlePrintReport(
                selectedGenus,
                generatedFeatureServiceUrl,
                selectedMethod,
                testSampleDataFromFile,
                fileDataHashMap,
                currentAnalysisId,
                dispatch,
                selectedOption
              )
            }
          >
            Download Report
          </button>
        </div>

        {showPostResultInfo && <ShowResult content={postResultInfoMessage} />}
      </div>

      <div className='right-side'>
        <div className='right-side-inner-container'>
          <div className='right-side-header'>
            <p>
              This map illustrates the geographic range of reference data used in the evaluation. It provides an
              overview of the available reference data 'area of insight' within the full genus distribution, to help you
              understand the conclusions of your evaluation.
            </p>
          </div>
          <div id='applicationDiv1' className='' ref={mapContainer} />
        </div>
      </div>
    </div>
  );
};

export default Verification;
